import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Link } from "react-router-dom";
import { RiArrowRightLine, RiDownload2Line } from "@remixicon/react";
import { Card, CardContent } from "@/components/card";
import { Button } from "@/components/button";
import { mainConfig } from "@/configs/main.config";

// Silo Data
const products = mainConfig.siloProducts.enterprise;
const pricingData = mainConfig.siloPricing;

export default function SiloEnterprisePage() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/silo">SILO</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Enterprise</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                  <h1 className="text-3xl font-bold">Enterprise</h1>
                </div>

                <a
                  href="http://ap.silos3.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <span>Access Enterprise Portal</span>
                  <RiArrowRightLine className="size-5" />
                </a>
              </div>

              <div className="mb-8">
                <p className="text-muted-foreground mb-4">
                  The price for object storage with SILO starts as low as $0.0036 per GB per month, with $0.0063 per GB for egress bandwidth.
                </p>
                <p className="text-muted-foreground">
                  The following table lists the types of metered services that appear in billing and usage user interfaces as well as invoices:
                </p>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
                <div className="lg:col-span-2">
                  <div className="flex items-center mb-6">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h3 className="text-xl font-medium">Products</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {products.map((product, index) => (
                      <Card key={index} className="bg-sidebar border border-border overflow-hidden hover:border-[#FF8A3C]/50 transition-all duration-300 h-full flex flex-col">
                        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF8A3C] to-[#FF8A3C]/70 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>

                        <CardContent className="p-6 flex flex-col h-full">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center mr-4 overflow-hidden">
                              <img
                                src={product.image}
                                alt={product.name}
                                width={48}
                                height={48}
                                className="object-contain"
                              />
                            </div>
                            <div>
                              <h4 className="text-lg font-semibold">{product.name}</h4>
                              <div className="text-xs px-2 py-1 rounded-full bg-muted inline-block">
                                {product.status === "Available" ? (
                                  <span className="text-green-500">{product.status}</span>
                                ) : (
                                  <span className="text-amber-500">{product.status}</span>
                                )}
                              </div>
                            </div>
                          </div>

                          <p className="text-sm text-muted-foreground mb-4 flex-grow">
                            {product.description}
                          </p>

                          <div className="mt-auto">
                            {product.links.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {product.links.map((link: { platform: string; url: string; status?: string }, linkIndex) => (
                                  <div key={linkIndex}>
                                    {link.status ? (
                                      <div className="text-sm text-muted-foreground flex items-center">
                                        <span className="mr-2">{link.platform}:</span>
                                        <span className="text-amber-500">{link.status}</span>
                                      </div>
                                    ) : (
                                      <a href={link.url} target="_blank" rel="noopener noreferrer">
                                        <Button variant="outline" size="sm" className="hover:border-[#FF8A3C] hover:text-[#FF8A3C]">
                                          <RiDownload2Line className="mr-1 size-4" />
                                          {link.platform}
                                        </Button>
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">
                                Coming soon
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                <div className="lg:col-span-1">
                  <div className="flex items-center mb-6">
                    <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                    <h3 className="text-xl font-medium">Pricing</h3>
                  </div>

                  <Card className="bg-sidebar border border-border overflow-hidden">
                    <CardContent className="p-6">
                      <div className="space-y-6">
                        {pricingData.map((item, index) => (
                          <div key={index} className="p-4 border border-border rounded-lg">
                            <div className="font-medium text-lg mb-2">{item.type}</div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
                              <div className="text-sm text-muted-foreground">Metered Units:</div>
                              <div className="text-sm">{item.units}</div>

                              <div className="text-sm text-muted-foreground">Increment:</div>
                              <div className="text-sm">{item.increment}</div>

                              <div className="text-sm text-muted-foreground">Pricing Unit:</div>
                              <div className="text-sm">{item.pricingUnit}</div>

                              <div className="text-sm text-muted-foreground">Price:</div>
                              <div className="text-sm text-[#FF8A3C] font-medium">{item.price}</div>
                            </div>
                            <div className="text-xs text-muted-foreground">{item.description}</div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 p-4 border border-border rounded-lg bg-muted/20">
                        <h4 className="font-medium mb-2">Enterprise Benefits</h4>
                        <ul className="text-sm text-muted-foreground space-y-2">
                          <li className="flex items-start">
                            <div className="h-4 w-4 rounded-full bg-[#FF8A3C]/20 flex items-center justify-center mt-0.5 mr-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-[#FF8A3C]"></div>
                            </div>
                            <span>Volume discounts available</span>
                          </li>
                          <li className="flex items-start">
                            <div className="h-4 w-4 rounded-full bg-[#FF8A3C]/20 flex items-center justify-center mt-0.5 mr-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-[#FF8A3C]"></div>
                            </div>
                            <span>Dedicated support team</span>
                          </li>
                          <li className="flex items-start">
                            <div className="h-4 w-4 rounded-full bg-[#FF8A3C]/20 flex items-center justify-center mt-0.5 mr-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-[#FF8A3C]"></div>
                            </div>
                            <span>Custom SLAs available</span>
                          </li>
                          <li className="flex items-start">
                            <div className="h-4 w-4 rounded-full bg-[#FF8A3C]/20 flex items-center justify-center mt-0.5 mr-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-[#FF8A3C]"></div>
                            </div>
                            <span>API access for all services</span>
                          </li>
                        </ul>
                      </div>

                      <div className="mt-6">
                        <a href="mailto:hi@larissa.network" className="w-full block">
                          <Button className="w-full bg-[#FF8A3C] text-white hover:bg-[#FF8A3C]/90">
                            Contact Sales
                          </Button>
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}





